import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../ducks/store';
import { ModuleTypes } from '../../utils/constants';
import DocViewer from '../common/DocViewer';
import EmbeddedViewer from '../common/EmbeddedViewer';
import Player from '../common/Player';
import classNames from 'classnames';

import './Module.scss';
import ReviewRetake from './ReviewRetake';

interface ModuleInstructionsIProps {
  instructions?: string;
}
const ModuleInstructions = ({ instructions }: ModuleInstructionsIProps) => {
  if (!instructions) {
    return null;
  }
  return (
    <div
      className="ModuleInstructions my-3 mx-1 mx-sm-3 mx-md-5 mx-lg-7 py-3 px-4"
      dangerouslySetInnerHTML={{ __html: instructions || '' }}
    />
  );
};

interface MockLearnerUITopBarIProps {
  tocIsOpen: boolean;
  moduleName?: string;
  reviewRetake?: boolean;
}
export const MockLearnerUITopBar = ({
  tocIsOpen,
  moduleName,
  reviewRetake,
}: MockLearnerUITopBarIProps) => (
  <div className={classNames('MockLearnerUITopBar d-flex text-left', { reviewRetake })}>
    <div
      className={classNames('MockLearnerUITopBar-module-name', { 'extra-toc-padding': !tocIsOpen })}
    >
      {moduleName}
    </div>
  </div>
);

interface IProps {
  courseId: string;
  moduleId: string;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  showReviewRetake: boolean;
  showToC: boolean;
  iframeOrigin: string;
  setInModule: () => void;
  viewed: () => void;
}

export const Module = ({
  courseId,
  moduleId,
  iframeRef,
  showReviewRetake,
  showToC,
  iframeOrigin,
  setInModule,
  viewed,
}: IProps) => {
  const { courseTitle, module, moduleLoading } = useSelector((state: RootState) => {
    const { learner } = state;
    const { course, moduleLoading } = learner;
    const module = course?.modules?.find((module) => module._id === moduleId);
    return { courseTitle: course?.title, module, moduleLoading };
  });
  const {
    title,
    type,
    authorId,
    video,
    document,
    embedded,
    isUnlocked,
    instructions,
    disableRetake,
  } = module || {};

  if (moduleLoading) return null;

  const iframeTitle = `${courseTitle} - Module: ${title}`;

  switch (type) {
    case ModuleTypes.Author:
      if (showReviewRetake && !disableRetake && courseId && moduleId) {
        return (
          <>
            <MockLearnerUITopBar tocIsOpen={showToC} moduleName={module?.title} reviewRetake />
            <ReviewRetake module={module} courseId={courseId} onClick={setInModule} />
          </>
        );
      } else {
        const queryParams = [`moduleId=${authorId}`, `useSession=true`];
        return (
          <iframe
            ref={iframeRef}
            title={iframeTitle}
            className="CourseViewer-author"
            src={`${iframeOrigin}/view?${queryParams.join('&')}`}
          ></iframe>
        );
      }
    case ModuleTypes.Document:
    case ModuleTypes.Video:
    case ModuleTypes.Embedded:
      return (
        <>
          <MockLearnerUITopBar tocIsOpen={showToC} moduleName={module?.title} />
          <ModuleInstructions instructions={instructions} />
          {type === ModuleTypes.Video && (
            <Player
              data={video}
              type="VIDEO"
              unlocked={isUnlocked}
              onPlayerEnded={viewed}
              className={classNames('VideoModule mx-1 mx-sm-3 mx-md-5 mx-lg-7', {
                'toc-open': showToC,
              })}
            />
          )}
          {type === ModuleTypes.Document && <DocViewer document={document} />}
          {type === ModuleTypes.Embedded && <EmbeddedViewer data={embedded} />}
        </>
      );
    default:
      return null;
  }
};
